import { cognitoConfig } from "@src/appV2/Auth/cognito/config";
import { getAppUrl } from "@src/appV2/Auth/LoginSignUp/libs/utils/getAppUrl";
import { Amplify } from "aws-amplify";
import {
  AuthSession,
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
  updateUserAttributes,
} from "aws-amplify/auth";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.clientId,
    },
  },
});

export interface CognitoUser {
  uid: string;
  email: string;
  emailVerified: boolean;
  phoneNumber?: string;
  displayName?: string;
  cbhUserId?: string;
}

export interface CognitoUserCredential {
  user: CognitoUser;
  idToken: string;
  refreshToken: string;
  accessToken?: string;
}

interface SignInWithFirebaseTokenParams {
  userIdentifier: string;
  firebaseIdToken: string;
}

interface Unsubscribe {
  (): void;
}

const CHALLENGE_TYPE = {
  MAGIC_LINK: "MAGIC_LINK",
  SMS_OTP: "SMS_OTP",
  FIREBASE_TOKEN_EXCHANGE: "FIREBASE_TOKEN_EXCHANGE",
} as const;

type ChallengeType = (typeof CHALLENGE_TYPE)[keyof typeof CHALLENGE_TYPE];

export class CognitoAuthentication {
  private tokenChangeListeners: Array<(user: CognitoUser | undefined) => Promise<void>> = [];
  private errorListeners: Array<(error: any) => void> = [];
  private challengeSession: string | null = null;

  // Initiate challenge selection (first step in custom auth flow)
  async initiateCustomAuth(username: string): Promise<{ challengeName: string; session?: string }> {
    try {
      const signInResult = await signIn({
        username,
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
          clientMetadata: {
            "client_metadata:custom_challenge:select": "init",
          },
        },
      });

      // Store session info for challenge response - Amplify handles this internally
      this.challengeSession = "amplify-managed-session";

      return {
        challengeName: signInResult.nextStep?.signInStep || "CUSTOM_CHALLENGE",
        session: this.challengeSession || undefined,
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Select challenge type (second step in custom auth flow)
  async selectChallengeType(challengeType: ChallengeType): Promise<{ session: string }> {
    try {
      const clientMetadata: Record<string, string> = {
        target_custom_challenge: challengeType,
        sub_challenge_step: "select",
      };

      // Add requester URL for magic link flow
      if (challengeType === CHALLENGE_TYPE.MAGIC_LINK) {
        const appUrl = getAppUrl();
        clientMetadata.requester_url = `${appUrl}/auth/redirect`;
      }

      const result = await confirmSignIn({
        challengeResponse: challengeType,
        options: {
          clientMetadata,
        },
      });

      // Check if the challenge response contains an error status
      // Challenge parameters can be in different locations depending on Amplify version
      const nextStepData = result.nextStep as any;
      const challengeParams =
        nextStepData?.additionalInfo || nextStepData?.challengeParameters || {};

      if (challengeParams.status === "error") {
        const error = new Error(challengeParams.message || "Challenge failed with error status");
        error.name = "ChallengeError";
        throw error;
      }

      return {
        session: "amplify-managed-session",
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Respond to custom challenge (final step - token/OTP confirmation)
  async respondToCustomChallenge(
    challengeAnswer: string,
    challengeType: ChallengeType
  ): Promise<CognitoUserCredential> {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: challengeAnswer,
        options: {
          clientMetadata: {
            target_custom_challenge: challengeType,
            sub_challenge_step: "answer",
            [`client_metadata:custom_challenge:${challengeType.toLowerCase()}`]: "verify",
          },
        },
      });

      // Check if the challenge response contains an error status
      // Challenge parameters can be in different locations depending on Amplify version
      const nextStepData = confirmResult.nextStep as any;
      const challengeParams =
        nextStepData?.additionalInfo || nextStepData?.challengeParameters || {};

      if (challengeParams.status === "error") {
        const error = new Error(challengeParams.message || "Challenge failed with error status");
        error.name = "ChallengeError";
        throw error;
      }

      if (confirmResult.isSignedIn) {
        const session = await fetchAuthSession();
        const user = this.extractUserFromSession(session);

        const credentials: CognitoUserCredential = {
          user,
          idToken: session.tokens?.idToken?.toString() || "",
          refreshToken: "", // Amplify handles refresh tokens internally
          accessToken: session.tokens?.accessToken?.toString(),
        };

        await this.emitTokenChange(user);
        return credentials;
      }

      throw new Error("Authentication not completed");
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Firebase-compatible methods (wrappers around custom auth flow)
  // Initiate magic link request (triggers email sending)
  async requestMagicLink(email: string): Promise<{ status: string; message: string }> {
    try {
      // Step 1: Initiate challenge selection
      const { session: initialSession } = await this.initiateCustomAuth(email);
      if (!initialSession) {
        throw new Error("Failed to get initial session");
      }

      // Step 2: Select MAGIC_LINK challenge type (this triggers email sending)
      await this.selectChallengeType(CHALLENGE_TYPE.MAGIC_LINK);

      return {
        status: "success",
        message: "Magic link sent to your email",
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Phone OTP flow using USER_AUTH (not custom challenge)
  async requestPhoneOtp(phoneNumber: string): Promise<{ status: string; message: string }> {
    try {
      await signIn({
        username: phoneNumber,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: CHALLENGE_TYPE.SMS_OTP,
        },
      });

      return {
        status: "success",
        message: "OTP sent to your phone",
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async confirmPhoneOtp(_phoneNumber: string, otpCode: string): Promise<CognitoUserCredential> {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: otpCode,
      });

      if (confirmResult.isSignedIn) {
        const session = await fetchAuthSession();
        const user = this.extractUserFromSession(session);

        const credentials: CognitoUserCredential = {
          user,
          idToken: session.tokens?.idToken?.toString() || "",
          refreshToken: "", // Amplify handles refresh tokens internally
          accessToken: session.tokens?.accessToken?.toString(),
        };

        await this.emitTokenChange(user);
        return credentials;
      }

      throw new Error("Authentication not completed");
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async signInWithEmailLink(link: string): Promise<CognitoUserCredential> {
    try {
      // Parse the magic link to extract the payload
      const url = new URL(link);
      const payload = url.searchParams.get("payload");
      if (!payload) {
        throw new Error("No payload found in magic link");
      }

      return await this.respondToCustomChallenge(payload, CHALLENGE_TYPE.MAGIC_LINK);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async signInWithFirebaseToken(
    params: SignInWithFirebaseTokenParams
  ): Promise<CognitoUserCredential> {
    const { userIdentifier, firebaseIdToken } = params;

    try {
      await this.initiateCustomAuth(userIdentifier);
      await this.selectChallengeType(CHALLENGE_TYPE.FIREBASE_TOKEN_EXCHANGE);
      return await this.respondToCustomChallenge(
        firebaseIdToken,
        CHALLENGE_TYPE.FIREBASE_TOKEN_EXCHANGE
      );
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async getCurrentUserInfo(): Promise<CognitoUser | undefined> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        return undefined;
      }
      return this.extractUserFromSession(session);
    } catch (error) {
      return undefined;
    }
  }

  get currentUser(): CognitoUser | undefined {
    // This will be handled by onIdTokenChanged listener
    return undefined;
  }

  async updateCurrentUserName(name: string): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          name,
        },
      });

      // Trigger token change to update cached user
      const session = await fetchAuthSession();
      const user = this.extractUserFromSession(session);
      await this.emitTokenChange(user);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async updateCurrentUserEmail(email: string): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          email,
        },
      });

      // Trigger token change to update cached user
      const session = await fetchAuthSession();
      const user = this.extractUserFromSession(session);
      await this.emitTokenChange(user);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  onIdTokenChanged(
    callback: (user: CognitoUser | undefined) => Promise<void>,
    errorCallback: (error: any) => void
  ): Unsubscribe {
    this.tokenChangeListeners.push(callback);
    this.errorListeners.push(errorCallback);

    // Initial call with current user
    this.getCurrentUserSession()
      .then((user) => callback(user))
      .catch(errorCallback);

    return () => {
      const callbackIndex = this.tokenChangeListeners.indexOf(callback);
      if (callbackIndex > -1) {
        this.tokenChangeListeners.splice(callbackIndex, 1);
      }

      const errorIndex = this.errorListeners.indexOf(errorCallback);
      if (errorIndex > -1) {
        this.errorListeners.splice(errorIndex, 1);
      }
    };
  }

  async signOut(): Promise<void> {
    try {
      await signOut();
      await this.emitTokenChange(undefined);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async initRecaptchaVerifier(): Promise<void> {
    // No-op for mobile compatibility
    return Promise.resolve();
  }

  async getIdToken(forceRefresh?: boolean): Promise<string | undefined> {
    const session = await fetchAuthSession({ forceRefresh });
    return session.tokens?.idToken?.toString();
  }

  private async getCurrentUserSession(): Promise<CognitoUser | undefined> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        return undefined;
      }
      return this.extractUserFromSession(session);
    } catch (error) {
      return undefined;
    }
  }

  private extractUserFromSession(session: AuthSession): CognitoUser {
    const idToken = session.tokens?.idToken;
    if (!idToken) {
      throw new Error("No ID token available");
    }

    // Parse JWT payload manually since we need the claims
    const payload = this.parseJwtPayload(idToken.toString());

    return {
      uid: payload.sub,
      email: payload.email || "",
      emailVerified: payload.email_verified || false,
      phoneNumber: payload.phone_number,
      displayName: payload.name,
      cbhUserId: payload["custom:cbh_user_id"],
    };
  }

  private parseJwtPayload(token: string): any {
    const [, payload] = token.split(".");
    if (token.split(".").length !== 3) {
      throw new Error("Invalid JWT format");
    }
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  }

  private async emitTokenChange(user: CognitoUser | undefined): Promise<void> {
    const promises = this.tokenChangeListeners.map((listener) =>
      listener(user).catch((error) => {
        this.emitError(error);
      })
    );
    await Promise.all(promises);
  }

  private emitError(error: any): void {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (listenerError) {
        // Ignore listener errors to prevent cascading failures
      }
    });
  }

  private mapAmplifyError(error: unknown): { code: string; message: string } {
    // If error is already a mapped error object, return it as-is
    if (error && typeof error === "object" && "code" in error && "message" in error) {
      return error as { code: string; message: string };
    }

    if (error && typeof error === "object" && "name" in error) {
      const amplifyError = error as { name: string; message: string };

      switch (amplifyError.name) {
        case "UserNotConfirmedException":
          return {
            code: "auth/email-not-verified",
            message: "Email address not verified",
          };
        case "NotAuthorizedException":
          return {
            code: "auth/wrong-password",
            message: "Invalid credentials",
          };
        case "UserNotFoundException":
          return {
            code: "auth/user-not-found",
            message: "No user found with this email",
          };
        case "InvalidParameterException":
          return {
            code: "auth/invalid-email",
            message: "Invalid email format",
          };
        case "TooManyRequestsException":
          return {
            code: "auth/too-many-requests",
            message: "Too many requests, please try again later",
          };
        case "NetworkError":
          return {
            code: "auth/network-request-failed",
            message: "Network error occurred",
          };
        case "ChallengeError":
          return {
            code: "auth/challenge-error",
            message: amplifyError.message || "Challenge failed",
          };
        default:
          return {
            code: "auth/unknown",
            message: amplifyError.message || "An unknown error occurred",
          };
      }
    }

    return {
      code: "auth/unknown",
      message: "An unknown error occurred",
    };
  }
}
